<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <!-- user form -->
        <q-form @submit="isEdit ? update() : create()" ref="userForm">
            <!-- username -->

            <q-input
                for="username-input"
                v-model="form.username"
                @clear="form.username = ''"
                :label="$t('Username')"
                :rules="[val => validateUsername(val)]"
                :disable="isEdit"
                class="q-mb-sm q-pb-md"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                bg-color="white"
                color="accent"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-user" />
                </template>
            </q-input>
            <!-- email -->
            <q-input
                for="email-input"
                v-model="form.email"
                :label="$t('Email')"
                @clear="form.email = ''"
                :rules="[val => validateEmail(val)]"
                class="q-mb-xs"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                bg-color="white"
                color="accent"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-envelope" />
                </template>
            </q-input>
            <!-- first name -->
            <q-input
                v-model="form.first_name"
                @clear="form.first_name = ''"
                :label="$t('First name')"
                :rules="[
                    val =>
                        val.length <= 30 ||
                        $t('First name cannot be longer than 30 characters')
                ]"
                class="q-mb-xs"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                for="first-name-input"
                bg-color="white"
                color="accent"
            />
            <!-- last name -->
            <q-input
                v-model="form.last_name"
                @clear="form.last_name = ''"
                :label="$t('Last name')"
                :rules="[
                    val =>
                        val.length <= 150 ||
                        $t('Last name cannot be longer than 150 characters')
                ]"
                class="q-mb-xs"
                style="width: 100%"
                clearable
                outlined
                lazy-rules
                for="last-name-input"
                bg-color="white"
                color="accent"
            />

            <q-field
                v-model="form.phone_number"
                class="q-mb-sm"
                style="width: 100%"
                :label="$t('Phone number')"
                borderless
                stack-label
                outlined
                :rules="[val => isPhoneValid || $t('Invalid number')]"
                bg-color="white"
                tag="div"
            >
                <template v-slot:prepend>
                    <q-icon name="fas fa-phone" />
                </template>
                <template v-slot:control>
                    <vue-tel-input
                        :wrapper-classes="[
                            'no-border',
                            'no-box-shadow',
                            'no-ouline'
                        ]"
                        class="input-vue-tel-input-in-qfield full-width"
                        style="height: 30px"
                        mode="international"
                        v-model="form.phone_number"
                        outlined
                        autoFormat
                        @validate="handlePhoneNumberInput"
                        validCharactersOnly
                    />
                </template>
            </q-field>

            <!-- job title -->
            <q-input
                for="job-title-input"
                v-model="form.job_title"
                @clear="form.job_title = ''"
                :rules="[
                    val =>
                        val.length <= 100 ||
                        $t('Job title cannot be longer than 100 characters')
                ]"
                :label="$t('Job title')"
                class="q-mb-xs"
                style="width: 100%"
                clearable
                outlined
                bg-color="white"
                color="accent"
            />

            <q-input
                v-model="form.report_default_start_time"
                :label="$t('Report default start time')"
                class="q-mb-lg"
                style="width: 100%"
                outlined
                mask="##:##:##"
                @click="timePickerShow = true"
                bg-color="white"
                color="accent"
            >
                <template v-slot:append>
                    <q-icon
                        @click="
                            () => {
                                timePickerShow = true;
                            }
                        "
                        name="access_time"
                        class="cursor-pointer"
                    ></q-icon>
                </template>
            </q-input>

            <q-dialog v-model="timePickerShow">
                <q-card>
                    <time-picker
                        v-model="time"
                        :with-seconds="true"
                    ></time-picker>
                    <div class="row q-ma-md">
                        <div align="right" class="col">
                            <q-btn
                                @click="timePickerShow = false"
                                :label="$t('Cancel')"
                                class="q-mr-sm bg-white"
                                text-color="primary"
                                outline
                            />
                            <q-btn
                                @click="handleTimePickerSelect"
                                :label="$t('Select')"
                                color="primary"
                            />
                        </div>
                    </div>
                </q-card>
            </q-dialog>

            <q-select
                v-model="form.default_time_picker"
                :label="$t('Default time picker')"
                :options="timePickerOptions"
                class="q-mb-lg"
                style="width: 100%"
                color="accent"
                emit-value
                map-options
                outlined
                bg-color="white"
            />
            <!-- role -->
            <q-select
                for="role-select"
                v-model="form.role"
                :disable="!hasEditPermission || isOwnAccount"
                :label="$t('Role')"
                :options="roleOptions"
                :rules="[val => (val && val !== '') || $t('Select a role')]"
                class="q-mb-xs"
                style="width: 100%"
                color="accent"
                emit-value
                map-options
                outlined
                bg-color="white"
            >
            </q-select>
            <!-- language -->
            <q-select
                for="language-select"
                v-model="form.language"
                :label="$t('Language')"
                :options="languages"
                :rules="[val => (val && val !== '') || $t('Select a language')]"
                class="q-mb-lg"
                color="accent"
                style="width: 100%"
                emit-value
                map-options
                outlined
                bg-color="white"
            />
            <!-- permission card -->
            <q-card class="permission-card" bordered flat>
                <q-card-section class="permission-card-section">
                    <q-toolbar class="text-secondary">
                        <q-icon name="fa fa-lock" color="5c5d5d"></q-icon>
                        <q-toolbar-title>
                            {{ $t('Permissions') }}
                        </q-toolbar-title>
                        <q-btn
                            color="primary"
                            v-show="hasEditPermission"
                            @click="showGrantAccessDialog = true"
                            >{{ $t('Grant Access') }}</q-btn
                        >
                    </q-toolbar>
                </q-card-section>
                <q-separator></q-separator>
                <q-card-section class="permission-card-section">
                    <!-- No access -->
                    <div
                        v-if="
                            !userAccess.groupedAccesses ||
                            userAccess.groupedAccesses.length === 0
                        "
                        class="q-mt-md row wrapper justify-center text-secondary vertical-middle"
                    >
                        <p>
                            <q-icon
                                name="fal fa-exclamation-triangle"
                                class="notation__icon"
                            />
                            {{
                                $t(
                                    'The user does not have access to any asset.'
                                )
                            }}
                        </p>
                    </div>
                    <!-- access list -->
                    <q-virtual-scroll
                        v-else
                        style="height: 300px"
                        :items="userAccess.groupedAccesses"
                        v-slot="{ item: facility, index }"
                    >
                        <q-item-label :key="facility.facility_id" header>
                            <q-item-section>
                                <q-item-label>
                                    <strong>
                                        {{ facility.facility_name }}
                                    </strong>
                                </q-item-label>
                            </q-item-section>
                        </q-item-label>
                        <q-item dense>
                            <q-item-section top>
                                <q-item-label caption>{{
                                    $t('Asset name')
                                }}</q-item-label>
                            </q-item-section>
                            <q-item-section top :side="!hasEditPermission">
                                <q-item-label caption>{{
                                    $t('Notification')
                                }}</q-item-label>
                            </q-item-section>
                            <q-item-section top side v-show="hasEditPermission">
                                <q-item-label caption>{{
                                    $t('Remove')
                                }}</q-item-label>
                            </q-item-section>
                        </q-item>
                        <q-item
                            v-for="access in facility.accesses"
                            :key="access.asset_id"
                            dense
                        >
                            <q-item-section top>
                                <q-item-label lines="1">{{
                                    access.asset_name
                                }}</q-item-label>
                            </q-item-section>

                            <q-item-section top :side="!hasEditPermission">
                                <q-item-label>
                                    <q-toggle
                                        :disable="
                                            ($userCannot('edit', 'users') ||
                                                $userCannot(
                                                    'always_dismiss',
                                                    'downtime_notifications'
                                                )) &&
                                            !isOwnAccount
                                        "
                                        v-model="access.is_notified"
                                        color="accent"
                                        size="md"
                                        class="q-mb-md"
                                        dense
                                    >
                                        <q-tooltip
                                            v-if="
                                                $userCannot(
                                                    'always_dismiss',
                                                    'downtime_notifications'
                                                ) && !isOwnAccount
                                            "
                                            style="font-size: 12px"
                                        >
                                            {{
                                                $t(
                                                    'Cannot change the notification preference of this user'
                                                )
                                            }}
                                        </q-tooltip>
                                        <q-tooltip
                                            v-else
                                            style="font-size: 12px"
                                        >
                                            {{
                                                $t(
                                                    'Allow this user to receive notifications from the selected asset'
                                                )
                                            }}
                                        </q-tooltip>
                                    </q-toggle>
                                </q-item-label>
                            </q-item-section>

                            <q-item-section top side v-show="hasEditPermission">
                                <q-item-label>
                                    <q-btn
                                        class="gt-xs"
                                        @click="removeAccess(access)"
                                        size="12px"
                                        color="negative"
                                        icon="delete"
                                        outline
                                        dense
                                    >
                                        <q-tooltip style="font-size: 12px">
                                            {{
                                                $t(
                                                    'Remove this user from the selected asset'
                                                )
                                            }}
                                        </q-tooltip></q-btn
                                    >
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                        <q-separator
                            inset
                            spaced
                            v-show="
                                index !== userAccess.groupedAccesses.length - 1
                            "
                        ></q-separator>
                    </q-virtual-scroll>
                </q-card-section>
            </q-card>
            <!-- action group -->
            <div class="row form__actions justify-between">
                <div class="q-col">
                    <q-btn
                        v-show="hasDeletePermission && isEdit"
                        @click="handleRemove()"
                        class="float-left q-px-xs q-mb-lg"
                        icon="fas fa-trash-alt"
                        color="negative"
                        outline
                    />
                    <q-space />
                </div>

                <div class="q-col">
                    <q-btn
                        @click="$router.go(-1)"
                        :label="$t('Cancel')"
                        class="q-mr-sm q-mb-lg bg-white"
                        text-color="primary"
                        outline
                    />
                    <q-btn
                        :label="$t('Save')"
                        :disable="!hasEditPermission"
                        class="q-mb-lg"
                        type="submit"
                        color="primary"
                        id="save-button"
                    />
                </div>
            </div>
        </q-form>
        <!-- grant access dialog -->
        <grant-access-dialog
            v-if="showGrantAccessDialog"
            @close="closeGrantAccessDialog"
            :accesses="availableAccesses"
        />
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import GrantAccessDialog from '@components/GrantAccessDialog.vue';
import { TIME_PICKER_OPTIONS } from '@models/timepickeroptions.js';
import { ROLES } from '@models/roles.js';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import TimePicker from '../components/TimePicker.vue';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';
import { useLanguagesStore } from '@stores/languages.js';

export default {
    name: 'UserDetail',
    mixins: [BreadcrumbsManager],
    components: { GrantAccessDialog, TimePicker, VueTelInput },
    props: {
        userId: {
            type: [Number, String],
            required: false
        }
    },
    async created() {
        // if editing the user detail
        if (this.isEdit) {
            // begin loading user
            this.beginLoading(`${this.$t('Loading user')}...`);
            // init the user form
            this.initForm();
            // get the available accesses of the edited user
            await this.getUserAccesses();
        }
        // group the edited user by facility
        this.userAccess.groupedAccesses = this.groupAccessesByFacility(
            this.userAccess.rawAccesses
        );
    },
    async mounted() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Settings'),
                route: {
                    name: 'settings'
                }
            },
            {
                label: this.$t('Users'),
                route: {
                    name: 'user_list'
                }
            },
            {
                label: this.isEdit
                    ? history.state.username
                    : this.$t('Create user account')
            }
        ]);
    },
    data() {
        return {
            TIME_PICKER_OPTIONS: TIME_PICKER_OPTIONS,
            form: {
                username: '',
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                job_title: '',
                report_default_start_time: '00:00:00',
                default_time_picker: 'digital',
                role: '',
                language: ''
            },
            isPhoneValid: true,
            userAccess: {
                rawAccesses: [],
                groupedAccesses: []
            },
            currentUserAccess: {
                rawAccesses: [],
                groupedAccesses: []
            },
            showGrantAccessDialog: false,
            timePickerShow: false,
            time: '00:00:00',
            email: ''
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            currentUser: 'user'
        }),
        ...mapState(useLanguagesStore, ['getLanguages']),
        /**
         * Get role options
         * @return {Object[]} - role options
         */
        roleOptions() {
            return Object.keys(ROLES).map(role_value => {
                return {
                    label: ROLES[role_value] || this.$t('Unknown'),
                    value: role_value
                };
            });
        },
        /**
         * Time Picker Options.
         */
        timePickerOptions() {
            return this.TIME_PICKER_OPTIONS.map(timePicker => {
                return {
                    label: this.$t(timePicker['label']),
                    value: timePicker['value']
                };
            });
        },
        /**
         * Language options that show label/keys.
         */
        languages() {
            return this.getLanguages.map(lang => {
                return {
                    label: `${lang['label']} / ${lang['value']}`,
                    value: lang['value']
                };
            });
        },
        /**
         * Determine if form is in edit mode.
         * @return {Boolean}
         */
        isEdit() {
            return !!this.userId;
        },
        /**
         * Check whether the current user has delete permission.
         * @return {Boolean}
         */
        hasDeletePermission() {
            return this.$userCan('delete', 'users');
        },
        /**
         * Check whether the current user has edit permission.
         * @return {Boolean}
         */
        hasEditPermission() {
            return this.$userCan('edit', 'users');
        },
        /**
         * Obtain the available accesses to grant
         * Available accesses = current user accesses - edited user accesses
         * @return {Object[]} - available accesses
         */
        availableAccesses() {
            if (
                this.userAccess.groupedAccesses &&
                this.currentUser &&
                this.currentUser['accesses']
            ) {
                return this.getAvailableAccesses(
                    this.currentUser['accesses'],
                    this.userAccess.groupedAccesses
                );
            }
            return null;
        },
        /**
         * Check a given id is the same as the current user id.
         * @return {Boolean} - true if the id is the same.
         */
        isOwnAccount() {
            if (this.userId && this.currentUser && this.currentUser['id']) {
                return this.userId == this.currentUser['id'];
            }
            return false;
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useAuthStore, ['setUserAction']),
        ...mapActions(useLanguagesStore, ['loadAvailableLanguages']),
        /**
         * Initialize form.
         */
        initForm() {
            this.form['username'] = history.state.username;
            this.form['email'] = history.state.email;
            this.form['phone_number'] = history.state.phone_number;
            this.form['first_name'] = history.state.first_name;
            this.form['last_name'] = history.state.last_name;
            this.form['job_title'] = history.state.job_title;
            this.form['report_default_start_time'] =
                history.state.report_default_start_time;
            this.form['default_time_picker'] =
                history.state.default_time_picker;
            this.form['role'] = history.state.role;
            this.form['language'] = history.state.language;

            // set current email to check if email is changed
            this.email = history.state.email;
        },
        /**
         * Obtain accesses list of the edited user from server.
         */
        async getUserAccesses() {
            await this.$axios
                .get(`users/${this.userId}/`)
                .then(response => {
                    this.userAccess.rawAccesses = Object.assign(
                        [],
                        response.data.accesses
                    );
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t('Failed to load user. Please try again later.'),
                        error
                    );
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Create user.
         */
        async create() {
            // begin loading user
            this.beginLoading(`${this.$t('Creating new user')}...`);
            // prepare the payload accepted by server
            const payload = {
                username: this.form['username'],
                first_name: this.form['first_name'],
                last_name: this.form['last_name'],
                email: this.form['email'],
                phone_number: this.form['phone_number'],
                job_title: this.form['job_title'],
                report_default_start_time:
                    this.form['report_default_start_time'],
                default_time_picker: this.form['default_time_picker'],
                role: this.form['role'],
                language: this.form['language'],
                accesses: this.ungroupAccesses(this.userAccess.groupedAccesses)
            };
            if (this.form.phone_number.replace(/ /g, '') !== '') {
                payload['phone_number'] = this.form.phone_number.replace(
                    / /g,
                    ''
                );
            } else {
                payload['phone_number'] = null;
            }
            // send query to server
            await this.$axios
                .post('users/', payload)
                .then(response => {
                    this.$notifySuccess(
                        this.$t('An invitation email was sent to the new user')
                    );
                    this.$router.go(-1);
                })
                .catch(e => {
                    const error =
                        e?.response?.data && typeof e.response.data === 'object'
                            ? e.response.data
                            : {};
                    if (
                        error['username'] &&
                        error['username'].length > 0 &&
                        error['username'][0] ===
                            'A user with that username already exists.'
                    ) {
                        const errorMessage = this.$t(
                            'An account with this username already exists.'
                        );
                        this.$notifyError(errorMessage, error['username'][0]);
                    } else if (
                        error['email'] &&
                        error['email'].length > 0 &&
                        error['email'][0] ===
                            'user with this email already exists.'
                    ) {
                        const errorMessage = this.$t(
                            'An account with this email already exists. Please use a different email.'
                        );
                        this.$notifyError(errorMessage, error['email'][0]);
                    } else if (
                        error['email'] &&
                        error['email'].length > 0 &&
                        error['email'].includes('email_domain_not_allowed')
                    ) {
                        const allowed_email_domains =
                            error['allowed_email_domains'];

                        if (allowed_email_domains.length > 0) {
                            this.$notifyError(
                                this.$t(
                                    'Email domain is not allowed for this client. Only the following domains are allowed: {allowed_email_domains}.',
                                    {
                                        allowed_email_domains:
                                            allowed_email_domains.join(', ')
                                    }
                                ),
                                error['email']
                            );
                        } else {
                            // If no allowed email domains are provided, fall back to generic error message
                            this.$notifyError(
                                this.$t(
                                    'Email domain is not allowed for this client.'
                                ),
                                error['email']
                            );
                        }
                    } else if (
                        error['error'] &&
                        error['error'] === 'create_user_role'
                    ) {
                        this.$notifyError(
                            this.$t(
                                'You do not have permission to create a user with this role.'
                            ),
                            error
                        );
                    } else {
                        this.$notifyError(
                            this.$t('Failed to create new user account'),
                            error
                        );
                    }
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Update user.
         */
        async update() {
            // begin loading user
            this.beginLoading(`${this.$t('Updating user detail')}...`);
            // define payload that is accepted by server
            const payload = {
                username: this.form['username'],
                first_name: this.form['first_name'],
                last_name: this.form['last_name'],
                email: this.form['email'],
                phone_number: this.form['phone_number'],
                job_title: this.form['job_title'],
                report_default_start_time:
                    this.form['report_default_start_time'],
                default_time_picker: this.form['default_time_picker'],
                role: this.form['role'],
                language: this.form['language'],
                accesses: this.ungroupAccesses(this.userAccess.groupedAccesses)
            };
            if (this.form.phone_number.replace(/ /g, '') !== '') {
                payload['phone_number'] = this.form.phone_number.replace(
                    / /g,
                    ''
                );
            } else {
                payload['phone_number'] = null;
            }
            // send query
            await this.$axios
                .put(`users/${this.userId}/`, payload)
                .then(response => {
                    this.$notifySuccess(
                        this.$t('The user details were successfully updated')
                    );
                    this.$router.go(-1);
                    // if changing the current user
                    // then update the profile of the use in vuex store
                    if (this.isOwnAccount) {
                        this.setUserAction(response.data);
                    }
                    // check if the email of the user is changed
                    const isEmailChanged = this.form['email'] !== this.email;
                    if (isEmailChanged) {
                        // notify the user to reactivate account
                        this.$notifySuccess(
                            this.$t('A reactivation link was sent to') +
                                ` ${this.form['email']}.`
                        );
                    }
                })
                .catch(e => {
                    const error =
                        e?.response?.data && typeof e.response.data === 'object'
                            ? e.response.data
                            : {};
                    if (
                        error['email'] &&
                        error['email'].length > 0 &&
                        error['email'].includes(
                            'user with this email already exists.'
                        )
                    ) {
                        const errorMessage = this.$t(
                            'An account with this email address already exists.'
                        );
                        this.$notifyError(errorMessage, error['email']);
                    } else if (
                        error['email'] &&
                        error['email'].length > 0 &&
                        error['email'].includes('email_domain_not_allowed')
                    ) {
                        const allowed_email_domains =
                            error['allowed_email_domains'];

                        if (allowed_email_domains.length > 0) {
                            this.$notifyError(
                                this.$t(
                                    'Email domain is not allowed for this client. Only the following domains are allowed: {allowed_email_domains}.',
                                    {
                                        allowed_email_domains:
                                            allowed_email_domains.join(', ')
                                    }
                                ),
                                error['email']
                            );
                        } else {
                            // If no allowed email domains are provided, fall back to generic error message
                            this.$notifyError(
                                this.$t(
                                    'Email domain is not allowed for this client.'
                                ),
                                error['email']
                            );
                        }
                    } else if (
                        error['error'] &&
                        error['error'] === 'control_target_role'
                    ) {
                        this.$notifyError(
                            this.$t(
                                "You don't have the permission to give this role."
                            ),
                            error
                        );
                    } else if (
                        error['error'] &&
                        error['error'] === 'control_current_role'
                    ) {
                        this.$notifyError(
                            this.$t(
                                "You do not have permission to modify the user's current role."
                            ),
                            error
                        );
                    } else if (
                        error['error'] &&
                        error['error'] === 'change_own_role'
                    ) {
                        this.$notifyError(
                            this.$t(
                                'You cannot change your own role. Please contact an administrator.'
                            ),
                            error
                        );
                    } else {
                        this.$notifyError(
                            this.$t('Failed to update user detail'),
                            error
                        );
                    }
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Handle remove user.
         */
        async handleRemove() {
            this.$confirmDelete(
                this.$t('Are you sure you want to remove this user account?')
            ).onOk(async () => {
                await this.removeUser();
            });
        },
        /**
         * Remove user. Triggered by handleRemove method.
         */
        async removeUser() {
            // begin loading
            this.beginLoading(`${this.$t('Deleting user')}...`);
            await this.$axios
                .delete(`users/${this.userId}/`)
                .then(response => {
                    this.$notifySuccess(
                        this.$t('The user was successfully removed')
                    );
                    this.$router.go(-1);
                })
                .catch(e => {
                    const error =
                        e?.response?.data && typeof e.response.data === 'object'
                            ? e.response.data
                            : {};
                    if (
                        error['error'] &&
                        (error['error'] === 'control_target_role' ||
                            error['error'] === 'control_current_role')
                    ) {
                        this.$notifyError(
                            this.$t(
                                'You do not have permission to delete a user with this role.'
                            ),
                            error
                        );
                    } else {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove user account. Please try again later.'
                            ),
                            error
                        );
                    }
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Add the access to the edited user's grouped accesses list.
         * @param {Object} - access
         */
        addAccess(access) {
            if (access) {
                // set a flag to check if the access has been added to user accesses list
                let isAdded = false;
                this.userAccess.groupedAccesses.forEach(facility => {
                    // if we find the right facility, push the access to its list of accesses
                    if (facility['facility_id'] === access['facility_id']) {
                        facility['accesses'].push({
                            access_id: access['access_id'],
                            asset_id: access['asset_id'],
                            asset_name: access['asset_name'],
                            is_notified: access['is_notified']
                        });
                        isAdded = true;
                    }
                });
                // if the access has not been added to the user accesses list
                // then create a new access list and save it
                if (!isAdded) {
                    this.userAccess.groupedAccesses.push({
                        facility_id: access['facility_id'],
                        facility_name: access['facility_name'],
                        accesses: [
                            {
                                access_id: access['access_id'],
                                asset_id: access['asset_id'],
                                asset_name: access['asset_name'],
                                is_notified: access['is_notified']
                            }
                        ]
                    });
                }
            }
        },
        /**
         * Remove the current access from accesses list.
         * @param {Object} - access
         */
        removeAccess(access) {
            this.$confirmDelete(
                this.$t(
                    'Are you sure you want to remove this user from the selected asset?'
                )
            ).onOk(() => {
                this.userAccess.groupedAccesses.forEach(facility => {
                    // then we need to find the right asset
                    facility['accesses'].forEach((item, index) => {
                        if (item['asset_id'] === access['asset_id']) {
                            // remove this item from the list
                            facility['accesses'].splice(index, 1);
                        }
                    });
                });
            });
        },
        /**
         * Validate the username input
         * @param {String} val - User input from username field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateUsername(val) {
            // false is an error
            if (val.length > 0) {
                // regular expression for username
                const regex = /^(?!.*\\)[a-zA-Z0-9@/.+_-]+$/;
                if (!regex.test(val)) {
                    return this.$t(
                        'Username should contain only letters, numbers, and {characters} characters.',
                        { characters: '@/.+/-/_' }
                    );
                }
            } else {
                return this.$t('Username cannot be empty');
            }
            return true;
        },
        /**
         * Validate the email input
         * @param {String} val - User input from email field.
         * @return {Boolean} - Valid inputs returns true.
         * @return {String} - Error message.
         */
        validateEmail(val) {
            // false is an error
            if (val.length > 0) {
                // regular expression for email

                const regex =
                    /^\w+([.-]?\w+)*(\+\w+)?@\w+([.-]?\w+)*(\.\w{2,3})+$/;
                if (!regex.test(val)) {
                    return this.$t('Invalid email format');
                }
            } else {
                return this.$t('Email cannot be empty');
            }
            return true;
        },
        /**
         * Handle the close action of the grant access dialog
         * @param {Object[]} - the accesses list that contains all the selected accesses
         */
        closeGrantAccessDialog(selectedAccesses) {
            //  go through the list of selectedAccesses and add every element to the accesses list
            if (selectedAccesses) {
                selectedAccesses.forEach(access => {
                    // add the access to the list
                    this.addAccess(access);
                });
            }
            // reset the grant access dialog
            this.showGrantAccessDialog = false;
        },
        /**
         * Group the list of accesses by facility id.
         * Returned data format:
         * [
         *    {
         *       "facility": 1, "facility_name": "Saskatoon",
         *       accesses: [
         *           {"access_id": 1, "asset_id": 1, "asset_name": "asset0", "is_notified": false},
         *           {"access_id": 2, "asset_id": 2, "asset_name": "asset1", "is_notified": true},
         *           {"access_id": 3, "asset_id": 5, "asset_name": "asset6", "is_notified": false},
         *       ]
         *    },
         *    { "facility": 2, "facility_name": "Georgetown",
         *       accesses: [
         *           {"access_id": 4, "asset_id": 7, "asset_name": "asset8", "is_notified": false},
         *           {"access_id": 5, "asset_id": 8, "asset_name": "asset9", "is_notified": true},
         *           {"access_id": 6, "asset_id": 9, "asset_name": "asset10", "is_notified": false}
         *       ]
         *    }
         * ]
         * @param {Object[]} inputAccesses - Raw access list
         * @return {Object[]} - access list that is grouped by facility id
         */
        groupAccessesByFacility(inputAccesses) {
            // corner case check
            if (!inputAccesses || inputAccesses.length === 0) {
                return [];
            }
            // define the result list
            let groupedAccesses = [];
            let accessMap = new Map();
            // create a sorted list (by facility id then asset id) before grouping them to ensure alphabetical order
            const sortedAccesses = [...inputAccesses].sort(
                (a, b) =>
                    a['facility_name'].localeCompare(b['facility_name']) ||
                    a['asset_name'].localeCompare(b['asset_name'])
            );
            // iterate the input access list and look every element up in the map
            sortedAccesses.forEach(access => {
                const facilityId = access['facility'];
                const assetId = access['asset'];
                if (!accessMap.has(facilityId)) {
                    accessMap.set(facilityId, {
                        facility_name: access['facility_name'],
                        accesses: []
                    });
                }
                accessMap.get(facilityId)['accesses'].push({
                    access_id: access['id'],
                    asset_id: access['asset'],
                    asset_name: access['asset_name'],
                    is_notified: access['is_notified'],
                    is_selected: access['is_selected']
                });
            });
            for (let key of accessMap.keys()) {
                groupedAccesses.push({
                    facility_id: key,
                    facility_name: accessMap.get(key)['facility_name'],
                    accesses: accessMap.get(key)['accesses']
                });
            }
            return groupedAccesses;
        },
        /**
         * Ungroup the list and prepare the list for query.
         * Returned data format:
         *  [
                { "facility": 1, "asset": 1, "is_notified": false },
                { "facility": 2, "asset": 4, "is_notified": false },
                { "facility": 3, "asset": 6, "is_notified": false },
            ]
            @param {Object[]} inputAccesses - input accesses list
            @return {Object[]} - the output list
         */
        ungroupAccesses(inputAccesses) {
            if (!inputAccesses) {
                return [];
            }
            let outputAccesses = [];
            inputAccesses.forEach(facility => {
                facility['accesses'].forEach(access => {
                    outputAccesses.push({
                        facility: facility['facility_id'],
                        asset: access['asset_id'],
                        is_notified: access['is_notified']
                    });
                });
            });
            return outputAccesses;
        },
        /**
         * Obtain the extra accesses.
         * The expected data format of the input accesses:
         *  [
         *     {
         *       "id": 123,"facility": 1,"facility_name": "West Jeanne","asset": 81,"asset_name": "asset0","is_notified": false
         *     },
         *     {
         *       "id": 122,"facility": 8,"facility_name": "North Ryanmouth","asset": 80,"asset_name": "asset79","is_notified": false
         *     }
         *  ]
         * The returned data format:
         *  [
         *    {
         *       "facility": 1, "facility_name": "Saskatoon",
         *       accesses: [
         *           {"access_id": 1, "asset_id": 1, "asset_name": "asset0", "is_notified": false},
         *           {"access_id": 2, "asset_id": 2, "asset_name": "asset1", "is_notified": true},
         *           {"access_id": 3, "asset_id": 5, "asset_name": "asset6", "is_notified": false},
         *       ]
         *    },
         *    { "facility": 2, "facility_name": "Georgetown",
         *       accesses: [
         *           {"access_id": 4, "asset_id": 7, "asset_name": "asset8", "is_notified": false},
         *           {"access_id": 5, "asset_id": 8, "asset_name": "asset9", "is_notified": true},
         *           {"access_id": 6, "asset_id": 9, "asset_name": "asset10", "is_notified": false}
         *       ]
         *    }
         * ]
         * @param {Object[]} granter - the accesses that is used to grant to selected user
         * @param {Object[]} grantee - the accesses of the selected user
         * @return {Object[]} - the extra Accesses
         */
        getAvailableAccesses(granter, grantee) {
            // deep copy the granter and grantee access list
            let granterAccesses = JSON.parse(JSON.stringify(granter));
            let granteeAccesses = JSON.parse(JSON.stringify(grantee));
            let availableAccesses = [];
            let granteeAccessSet = new Set();
            // prepare the grantee accesses set
            granteeAccesses.forEach(facility => {
                facility['accesses'].forEach(access => {
                    if (!granteeAccessSet.has(access['asset_id'])) {
                        granteeAccessSet.add(access['asset_id']);
                    }
                });
            });
            // push the element in granter accesses to result list
            // if it does not exist in set
            granterAccesses.forEach(access => {
                if (!granteeAccessSet.has(access['asset'])) {
                    access['is_notified'] = false;
                    access['is_selected'] = false;
                    availableAccesses.push(access);
                }
            });
            return this.groupAccessesByFacility(availableAccesses);
        },
        /**
         * Vue-tel functions
         */
        handlePhoneNumberInput(phoneObject) {
            if (phoneObject['valid'] === false) {
                this.isPhoneValid = phoneObject['valid'];
            } else {
                this.isPhoneValid = true;
            }
        },
        handleTimePickerSelect() {
            this.timePickerShow = false;
            this.form.report_default_start_time = this.time;
        }
    }
};
</script>

<style lang="scss" scoped>
.form__actions {
    margin-bottom: 25px;
    margin-top: 50px;
}
.permission-card-section {
    padding: 2px;
}
.notation__icon {
    font-size: 200%;
    margin-right: 8px;
}
/* note: the height 98% is to avoid the inner input to hide the bottom border of q-field */
.input-vue-tel-input-in-qfield {
    height: 98%;
    width: 98%;
    margin-bottom: auto;
    border-style: none;
}
/* note: I use color dark for my text, I am forcing it because otherwise it is the same color as q-field - so here primary color or negative color when error - */
.wrapper-vue-tel-input-in-qfield li.vti__dropdown-item {
    color: var(--q-color-dark);
}
</style>
